import { escapeRegExp } from "lodash";
import type { SetterOrUpdater } from "recoil";

import { BOOKING_DIRECTIONS_MULTIPLIER } from "../constants";
import type {
  BookingDto,
  IBookingCalendarPassengerDto,
  IBulkSetBookingsPassengerDto,
  IPassengerDto,
} from "../dto";
import {
  BookingCalendarViewMode,
  DirectionEnum,
  SelectionEnum,
  type PassengerDeleteResultEnum,
} from "../enums";
import type {
  BookingCellId,
  BulkBookingCreateConflict,
  DateYMDString,
  IApprovalBookingConflict,
  IBookingCalendarParsedCell,
  TBookingFieldName,
} from "../models";
import { isValidDateTime } from "./date.helpers";
import { Log } from "./log.helpers";
import { isPhoneSearchMatch } from "./phone.helpers";

export const BOOKING_CELL_ID_NO_DATA: BookingCellId = "1900-01-01_0";
export const BOOKING_CELL_PARSED_NO_DATA: IBookingCalendarParsedCell = {
  passengerId: 0,
  date: "1900-01-01",
};

export const toBookingCellId = (
  passengerId: number,
  dateYYYYMMDD: DateYMDString,
): BookingCellId => {
  if (!passengerId || !dateYYYYMMDD || !isValidDateTime(dateYYYYMMDD)) {
    return BOOKING_CELL_ID_NO_DATA;
  }

  return `${dateYYYYMMDD}_${passengerId}`;
};

export const toBookingCellIdParsedCell = (
  cell: IBookingCalendarParsedCell,
): BookingCellId => {
  return `${cell.date}_${cell.passengerId}`;
};

export const parseBookingCellId = (
  cellId: BookingCellId,
): IBookingCalendarParsedCell => {
  if (!cellId?.includes("_")) {
    return BOOKING_CELL_PARSED_NO_DATA;
  }

  const parsed = cellId.split("_");
  const result: IBookingCalendarParsedCell = {
    date: parsed[0] as DateYMDString,
    passengerId: +parsed[1],
  };
  return result;
};

export const deleteBookingsActionsTransform = (
  deleteBookingAction: Map<number, PassengerDeleteResultEnum>,
): [number, PassengerDeleteResultEnum][] | undefined => {
  const actionsTransformed: [number, PassengerDeleteResultEnum][] | undefined =
    deleteBookingAction?.size
      ? [...deleteBookingAction].map(([key, val]) => {
          return [key, val];
        })
      : undefined;

  return actionsTransformed;
};

interface IHandlerArguments {
  setter: SetterOrUpdater<Set<BookingCellId>>;
  rowOrCol: Set<BookingCellId>;
}

const selectAllRow = ({ setter, rowOrCol }: IHandlerArguments): void => {
  setter(prevValue => new Set([...prevValue, ...rowOrCol]));
};

const deselectRow = ({ setter, rowOrCol }: IHandlerArguments): void => {
  setter(prevValue => {
    const tmpValue = new Set(prevValue);
    rowOrCol.forEach(bookingCellId => {
      tmpValue.delete(bookingCellId);
    });
    return new Set(tmpValue);
  });
};

export const setCheckedHandler: Record<
  SelectionEnum,
  (args: IHandlerArguments) => void
> = {
  [SelectionEnum.All]: selectAllRow,
  [SelectionEnum.Some]: selectAllRow,
  [SelectionEnum.None]: deselectRow,
};

export const isPassengerAllowedByViewMode = ({
  mode,
  passengerId,
  passengerIdBookingMap,
}: {
  mode: BookingCalendarViewMode;
  passengerId: number;
  passengerIdBookingMap: Map<number, BookingDto[]>;
}): boolean => {
  switch (mode) {
    case BookingCalendarViewMode.WithBookings:
      return passengerIdBookingMap.has(passengerId);
    case BookingCalendarViewMode.WithoutBookings:
      return !passengerIdBookingMap.has(passengerId);
    default:
      return true;
  }
};

export const getBookingCalendarCellId = (
  passengerId: number,
  dateYMD: DateYMDString,
): BookingCellId => `${dateYMD}_${passengerId}`;

export const resolveIsBookingRestrictedBySearch = ({
  passengerId,
  passengerIdBookingsMap,
  searchValue,
  isPassengerHasBookings,
}: {
  passengerId: number;
  passengerIdBookingsMap: Map<number, BookingDto[]>;
  searchValue: string;
  isPassengerHasBookings: boolean;
}): boolean => {
  if (!isPassengerHasBookings) {
    return false;
  }

  const bookings = passengerIdBookingsMap.get(passengerId) as BookingDto[];

  const isAnyMatch = bookings.some(booking =>
    String(booking.id).includes(searchValue),
  );

  return isAnyMatch;
};

export const getIsCalendarPassengerWorkerCodeRestrictedBySearch = ({
  passenger,
  searchValue,
}: {
  passenger: IBookingCalendarPassengerDto | IPassengerDto | null;
  searchValue: string;
}): boolean =>
  Boolean(
    passenger?.code
      ?.toLowerCase()
      ?.match(escapeRegExp(searchValue).toLowerCase()),
  );

// Also hit by the local form ("052..."), which is how the phone is shown.
export const getIsCalendarPassengerPhoneRestrictedBySearch = ({
  passenger,
  searchValue,
}: {
  passenger: IBookingCalendarPassengerDto | IPassengerDto | null;
  searchValue: string;
}): boolean =>
  Boolean(
    passenger?.phoneMobile
      ?.toLowerCase()
      ?.match(escapeRegExp(searchValue).toLowerCase()),
  ) || isPhoneSearchMatch({ rawPhone: passenger?.phoneMobile, searchValue });

export const resolveDirection = ({
  isPickUpDirection,
  isDropOffDirection,
}: {
  isPickUpDirection: boolean;
  isDropOffDirection: boolean;
}): DirectionEnum => {
  switch (true) {
    case isPickUpDirection && isDropOffDirection:
      return DirectionEnum.Both;
    case isPickUpDirection:
      return DirectionEnum.PickUp;
    case isDropOffDirection:
      return DirectionEnum.DropOff;
    default:
      Log.error(
        `Can't resolve direction - isPickUpDirection:${isPickUpDirection} isDropOffDirection:${isDropOffDirection}`,
      );
      return DirectionEnum.None;
  }
};

export const getDirectionAndBookingIds = (
  bookings: BookingDto[],
): [direction: DirectionEnum, bookingIds: number[]] => {
  let isPickUpDirection = false;
  let isDropOffDirection = false;
  const bookingsIds = bookings?.map(booking => {
    if (booking.direction === DirectionEnum.PickUp) {
      isPickUpDirection = true;
    }

    if (booking.direction === DirectionEnum.DropOff) {
      isDropOffDirection = true;
    }

    return booking.id;
  });
  const direction = resolveDirection({ isPickUpDirection, isDropOffDirection });

  return [direction, bookingsIds];
};

/** The booking field holding the passenger's home side: pickup going to work (A), dropoff coming back (B). */
export const getBookingHomeStationFieldName = (
  direction: DirectionEnum | undefined,
): TBookingFieldName | undefined => {
  switch (direction) {
    case DirectionEnum.PickUp:
      return "originStationId";
    case DirectionEnum.DropOff:
      return "destinationStationId";
    default:
      return undefined;
  }
};

export const getApprovalBookingConflictFromConflict = (
  errorConflict: BulkBookingCreateConflict,
): IApprovalBookingConflict => {
  return {
    id: errorConflict.id,
    bookingId: errorConflict.bookingId,
    passengerId: errorConflict.passengerId,
    passengerFullName: errorConflict.passengerFullName,
    displayDirection: errorConflict.displayDirection,
    errorType: errorConflict.errorType,
    errorAdditional: errorConflict.errorAdditional,
    approvalReason: null,
    approver: null,
    date: errorConflict.date,
  };
};

export const getDirectionFromPassengersData = ({
  passengersData,
}: {
  passengersData: IBulkSetBookingsPassengerDto[];
}): DirectionEnum => {
  let isPickUpDirection = false;
  let isDropOffDirection = false;

  passengersData.forEach(data => {
    if (data.direction === DirectionEnum.Both) {
      isPickUpDirection = true;
      isDropOffDirection = true;
    }

    if (data.direction === DirectionEnum.PickUp) {
      isPickUpDirection = true;
    }

    if (data.direction === DirectionEnum.DropOff) {
      isDropOffDirection = true;
    }
  });

  return resolveDirection({ isPickUpDirection, isDropOffDirection });
};

export const getBookingsCountFromPassengersData = ({
  passengersData,
}: {
  passengersData: IBulkSetBookingsPassengerDto[];
}): number => {
  return passengersData.reduce((acc, passenger) => {
    const directionCount =
      passenger.direction === DirectionEnum.Both
        ? BOOKING_DIRECTIONS_MULTIPLIER
        : 1;
    return acc + directionCount;
  }, 0);
};

/**
 * How many bookings a bulk upload is shown as creating. RYDE-8171: the backend collapses a row on a meal-only shift
 * (even an AB one) into a single direction X booking and counts it once in the polling `totalCount`; the uploaded rows
 * alone cannot tell a meal-only shift apart. So the server total wins once a run has reported it, and until then each
 * AB row counts two (the server counts a regular row the same way).
 */
export const resolveBulkUploadBookingsCount = ({
  passengersData,
  pollingTotalCount,
}: {
  passengersData: IBulkSetBookingsPassengerDto[];
  pollingTotalCount?: number;
}): number =>
  pollingTotalCount ?? getBookingsCountFromPassengersData({ passengersData });
